import { assignInlineVars } from '@vanilla-extract/dynamic';
import { cx } from 'emotion';
import {
  useMemo, useCallback, ReactNode, CSSProperties,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { sendAnalytics } from '@ezugi/analytics/sendAnalytics';
import { SIZES } from '@ezugi/constants';
import { Icon } from '@ezugi/primitives';
import doubleArrowDown from '@ezugi/static/icons/double-arrow-down.svg';
import { DeviceInfo } from '@ezugi/utils';

import { soundActions, uiActions } from '../../store/actions';
import { HIDE_UI } from '../../store/constants/analytics';
import { boardToggledSelector, desktopBoardToggleHeightSelector } from '../../store/selectors';
import {
  gameBoardContainer, toggledGameBoard, toggledIcon, toggledTranslateHeight, toggleStyle,
} from './BoardToggle.css';

/* Board Toggle should be enabled on mobile devices at all times */

type BoardToggleProps = {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  mobileHeight?: string;
  enableToggle?: boolean;
};

function BoardToggle({
  children,
  className,
  style: externalStyle,
  mobileHeight = '100%',
  enableToggle = false,
}: BoardToggleProps) {
  const toggled = useSelector(boardToggledSelector);
  const desktopBoardToggleHeight = useSelector(desktopBoardToggleHeightSelector);
  const dispatch = useDispatch();
  const style = useMemo(
    () => ({
      ...assignInlineVars({
        [toggledTranslateHeight]: DeviceInfo.isHandheld
          ? mobileHeight
          : `calc(${desktopBoardToggleHeight
           }% - ${SIZES.HEADER_DESKTOP_HEIGHT}px)`,
      }),
      ...externalStyle,
    }),
    [ externalStyle, desktopBoardToggleHeight, mobileHeight ]
  );

  const handleOnClick = useCallback(() => {
    dispatch(uiActions.ui.toggleBoard());
    sendAnalytics({
      event: HIDE_UI,
      // eslint-disable-next-line camelcase
      hide_ui: !toggled,
    });

    dispatch(
      soundActions.sound.play({ soundName: 'click', spriteName: 'base_sprite' })
    );
  }, [ toggled, dispatch ]);

  return !DeviceInfo.isHandheld && !enableToggle ? (
    children
  ) : (
    <div
      style={style}
      className={cx(
        gameBoardContainer,
        { [toggledGameBoard]: toggled },
        className
      )}
    >
      {!DeviceInfo.isHandheld && (
        <div
          className={toggleStyle}
          data-e2e="game-board-toggle"
          onClick={handleOnClick}
        >
          <Icon
            svg={doubleArrowDown} size={24}
            className={cx({ [toggledIcon]: toggled })}
          />
        </div>
      )}
      {children}
    </div>
  );
}

export default BoardToggle;
